import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Spinner } from "@heroui/react";
import R1FilterBar from "../components/R1FilterBar";
import { useR1DashboardContext } from "../context/R1DashboardProvider";
import { r1Api } from "../services/api";
import { MOST_RECENT } from "../constants";
import { getQuarterFromYYYYMM } from "../utils/quarters";
import { ScrollableX } from "../components/ScrollableX";

// ─── Types ────────────────────────────────────────────────────────────────────

type Row = Record<string, unknown>;

interface IssuerGroup {
  issuerId: number;
  issuerInfo: Row;
  positions: Row[];
}

// ─── Formatters ───────────────────────────────────────────────────────────────

const fmtText = (v: unknown) =>
  v === null || v === undefined || v === "" ? "—" : String(v);

const fmtNum = (v: unknown) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = parseFloat(String(v));
  return isNaN(n)
    ? String(v)
    : new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n);
};

const fmtPct = (v: unknown) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = parseFloat(String(v));
  return isNaN(n) ? String(v) : (n * 100).toFixed(2) + "%";
};

const fmtMult = (v: unknown) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = parseFloat(String(v));
  return isNaN(n) ? String(v) : n.toFixed(2) + "x";
};

const fmtBool = (v: unknown) => {
  if (v === null || v === undefined) return "—";
  return v ? "Yes" : "No";
};

// ─── Column definitions ───────────────────────────────────────────────────────

interface ColDef {
  key: string;
  header: string;
  size: number;
  fmt: (v: unknown) => string;
  align?: "right";
  lastIssuer?: boolean;
}

const ISSUER_COLS: ColDef[] = [
  { key: "brand_name", header: "Short Name", size: 140, fmt: fmtText },
  { key: "website", header: "Website", size: 160, fmt: fmtText },
  { key: "legal_form", header: "Legal Form", size: 140, fmt: fmtText },
  {
    key: "seed_pool_raw",
    header: "Seedpool / Fund Inv.",
    size: 180,
    fmt: fmtText,
  },
  { key: "nace_code", header: "NACE Code", size: 100, fmt: fmtText },
  { key: "sector", header: "Sector", size: 130, fmt: fmtText },
  {
    key: "country_of_domicile",
    header: "Geography (Legal Ctry)",
    size: 140,
    fmt: fmtText,
  },
  { key: "city", header: "City", size: 110, fmt: fmtText },
  {
    key: "company_with_connection_to_germany",
    header: "Connection to Germany",
    size: 100,
    fmt: fmtBool,
  },
  {
    key: "commercial_register_number",
    header: "Commercial Register Nr.",
    size: 155,
    fmt: fmtText,
  },
  {
    key: "pc_description",
    header: "Short Description",
    size: 200,
    fmt: fmtText,
  },
  { key: "lei_code", header: "LEI Code", size: 160, fmt: fmtText },
  {
    key: "change_indicator_1",
    header: "Change Indicator 1",
    size: 120,
    fmt: fmtText,
  },
  {
    key: "change_indicator_2",
    header: "Change Indicator 2",
    size: 180,
    fmt: fmtText,
  },
  { key: "drs", header: "DRS", size: 60, fmt: fmtBool },
  { key: "is_listed", header: "Listed", size: 65, fmt: fmtBool },
  { key: "r1_gp_id_pc", header: "GP ID", size: 90, fmt: fmtText },
  {
    key: "pc_new_in_total_portfolio",
    header: "New in Portfolio",
    size: 105,
    fmt: fmtBool,
  },
  {
    key: "comment",
    header: "Comment",
    size: 180,
    fmt: fmtText,
    lastIssuer: true,
  },
];

const POSITION_COLS: ColDef[] = [
  { key: "reporting_date", header: "Reporting Date", size: 115, fmt: fmtText },
  { key: "asset_type", header: "Asset Type", size: 140, fmt: fmtText },
  { key: "company_investment_state", header: "State", size: 90, fmt: fmtText },
  {
    key: "reporting_currency_fund",
    header: "Fund Ccy",
    size: 75,
    fmt: fmtText,
  },
  { key: "investment_currency", header: "Inv. Ccy", size: 75, fmt: fmtText },
  {
    key: "total_investment_cost",
    header: "Total Cost",
    size: 130,
    fmt: fmtNum,
    align: "right",
  },
  {
    key: "current_costs",
    header: "Active Cost",
    size: 120,
    fmt: fmtNum,
    align: "right",
  },
  {
    key: "proceeds",
    header: "Proceeds",
    size: 110,
    fmt: fmtNum,
    align: "right",
  },
  {
    key: "realized_cost",
    header: "Realized Cost",
    size: 115,
    fmt: fmtNum,
    align: "right",
  },
  {
    key: "dividends_income",
    header: "Dividends",
    size: 110,
    fmt: fmtNum,
    align: "right",
  },
  {
    key: "fair_value",
    header: "Fair Value",
    size: 120,
    fmt: fmtNum,
    align: "right",
  },
  {
    key: "stake_non_diluted",
    header: "Stake %",
    size: 80,
    fmt: fmtPct,
    align: "right",
  },
  {
    key: "stake_fully_diluted",
    header: "Stake % (FD)",
    size: 90,
    fmt: fmtPct,
    align: "right",
  },
  { key: "irr", header: "IRR", size: 75, fmt: fmtPct, align: "right" },
  { key: "moic", header: "MOIC", size: 70, fmt: fmtMult, align: "right" },
  {
    key: "voting_rights",
    header: "Voting Rights",
    size: 90,
    fmt: fmtPct,
    align: "right",
  },
  {
    key: "first_investment_date",
    header: "First Inv. Date",
    size: 125,
    fmt: fmtText,
  },
  {
    key: "date_of_partial_exit",
    header: "Partial Exit Date",
    size: 125,
    fmt: fmtText,
  },
  { key: "exit_date", header: "Exit Date", size: 100, fmt: fmtText },
  { key: "exit_method", header: "Exit Method", size: 120, fmt: fmtText },
];

const ALL_COLS = 1 + ISSUER_COLS.length + POSITION_COLS.length;

// ─── State badge ──────────────────────────────────────────────────────────────

function StateBadge({ value }: { value: unknown }) {
  if (!value || value === "—") return <span className="text-gray-400">—</span>;
  const s = String(value).toLowerCase();
  if (s.includes("exit"))
    return (
      <span className="inline-flex rounded-full border border-red-100 bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-600">
        {String(value)}
      </span>
    );
  return (
    <span className="inline-flex rounded-full border border-emerald-100 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
      {String(value)}
    </span>
  );
}

function CellValue({ col, row }: { col: ColDef; row: Row }) {
  if (col.key === "company_investment_state")
    return <StateBadge value={row[col.key]} />;
  return <>{col.fmt(row[col.key])}</>;
}

// ─── Period helpers ───────────────────────────────────────────────────────────

function matchesPeriod(row: Row, period: string | null): boolean {
  if (!period) return true;
  return getQuarterFromYYYYMM(row.reporting_date) === period;
}

// ─── Main view ────────────────────────────────────────────────────────────────

export default function PositionsIssuersView() {
  const { selectedFundIds, selectedPeriod, periodsPerFund } =
    useR1DashboardContext();
  const activeFundId = selectedFundIds[0] ?? null;
  const isMostRecent = selectedPeriod === MOST_RECENT;

  const { data, isLoading } = useQuery({
    queryKey: ["r1", "positions-issuers", activeFundId],
    queryFn: () => r1Api.getPositionsIssuers(activeFundId!),
    enabled: !!activeFundId,
    staleTime: 60_000,
  });

  const issuerIds = useMemo(() => {
    const ids = new Set<number>();
    for (const row of (data?.data ?? []) as Record<string, unknown>[]) {
      const id = Number(row.issuer_id);
      if (Number.isFinite(id) && id > 0) ids.add(id);
    }
    return Array.from(ids).sort((a, b) => a - b);
  }, [data]);

  const { data: issuersData } = useQuery({
    queryKey: ["r1", "issuers-by-ids", issuerIds],
    queryFn: () => r1Api.getIssuers({ ids: issuerIds }),
    staleTime: 60_000,
    enabled: issuerIds.length > 0,
  });

  const issuerMap = useMemo(() => {
    const map = new Map<number, Row>();
    for (const iss of (issuersData?.data ?? []) as Row[]) {
      map.set(Number(iss.id ?? iss.issuer_id), iss);
    }
    return map;
  }, [issuersData]);

  const groups = useMemo<IssuerGroup[]>(() => {
    if (!data?.data) return [];
    const allRows = data.data as Row[];

    const periodToMatch = isMostRecent
      ? (periodsPerFund.get(activeFundId!)?.[0] ?? null)
      : selectedPeriod;
    const rows = allRows.filter((row) => matchesPeriod(row, periodToMatch));

    const order: number[] = [];
    const map = new Map<number, IssuerGroup>();
    for (const row of rows) {
      const id = Number(row.issuer_id);
      if (!map.has(id)) {
        order.push(id);
        map.set(id, {
          issuerId: id,
          issuerInfo: issuerMap.get(id) ?? row,
          positions: [],
        });
      }
      map.get(id)!.positions.push(row);
    }
    return order.map((id) => map.get(id)!);
  }, [
    data,
    issuerMap,
    selectedPeriod,
    isMostRecent,
    periodsPerFund,
    activeFundId,
  ]);

  const totalPositions = groups.reduce((s, g) => s + g.positions.length, 0);

  if (selectedFundIds.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <R1FilterBar singleSelect />
        <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white text-sm text-gray-400">
          Select a fund above
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <R1FilterBar singleSelect />
        <div className="flex h-48 items-center justify-center">
          <Spinner label="Loading…" color="primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <R1FilterBar singleSelect />

      {/* Title + stats */}
      <div className="flex items-center justify-between px-1">
        <h1 className="font-semibold text-xl text-primary">
          Positions — Issuers
        </h1>
        <div className="rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] font-medium text-gray-500 shadow-sm">
          {groups.length} issuers · {totalPositions} positions
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <ScrollableX>
          <table className="w-full text-xs border-collapse">
            <thead>
              {/* Group label row */}
              <tr className="bg-gray-50 border-b border-gray-200">
                <th
                  className="sticky left-0 z-20 bg-gray-50 px-4 py-1.5 border-r border-gray-200"
                  style={{ minWidth: 240 }}
                />
                <th
                  colSpan={ISSUER_COLS.length}
                  className="px-4 py-1.5 text-left text-[10px] font-bold uppercase tracking-widest text-gray-400 border-r-2 border-gray-200"
                >
                  Issuer
                </th>
                <th
                  colSpan={POSITION_COLS.length}
                  className="border-r border-gray-200 px-4 py-1.5 text-left text-[10px] font-bold uppercase tracking-widest text-gray-400"
                >
                  Position
                </th>
              </tr>

              {/* Column headers */}
              <tr className="bg-gray-50 border-b-2 border-gray-200">
                <th
                  className="sticky left-0 z-20 bg-gray-50 px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider text-gray-500 border-r border-gray-200 whitespace-nowrap"
                  style={{ minWidth: 240 }}
                >
                  Company
                </th>
                {ISSUER_COLS.map((col) => (
                  <th
                    key={col.key}
                    className={`px-3 py-2.5 text-[11px] font-bold uppercase tracking-wider text-gray-500 whitespace-nowrap bg-gray-50 ${
                      col.lastIssuer ? "border-r-2 border-gray-200" : ""
                    } ${col.align === "right" ? "text-right" : "text-left"}`}
                    style={{ minWidth: col.size }}
                  >
                    {col.header}
                  </th>
                ))}
                {POSITION_COLS.map((col, index) => (
                  <th
                    key={col.key}
                    className={`px-3 py-2.5 text-[11px] font-bold uppercase tracking-wider text-gray-500 whitespace-nowrap bg-gray-50 ${
                      col.align === "right" ? "text-right" : "text-left"
                    } ${index === POSITION_COLS.length - 1 ? "border-r border-gray-200" : ""}`}
                    style={{ minWidth: col.size }}
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {groups.length === 0 && (
                <tr>
                  <td
                    colSpan={ALL_COLS}
                    className="py-12 text-center text-sm text-gray-400"
                  >
                    No data found for this fund / period
                  </td>
                </tr>
              )}

              {groups.map((group, gi) => {
                const info = group.issuerInfo;
                const companyName = String(
                  info.company_name_long ??
                    info.brand_name ??
                    `Issuer ${group.issuerId}`,
                );

                return group.positions.map((pos, pi) => {
                  const isFirst = pi === 0;
                  const isLastInGroup = pi === group.positions.length - 1;
                  const groupBorderTop =
                    gi > 0 && isFirst
                      ? "border-t-2 border-gray-300"
                      : "border-t border-gray-100";

                  return (
                    <tr
                      key={`${group.issuerId}-${pi}`}
                      className={`${groupBorderTop} hover:bg-blue-50/30 transition-colors`}
                    >
                      {/* Rowspanned company name — only rendered on first position row */}
                      {isFirst && (
                        <td
                          rowSpan={group.positions.length}
                          className="sticky left-0 z-10 bg-white px-4 py-2.5 border-r border-gray-200 align-top"
                          style={{
                            minWidth: 240,
                            borderBottom: isLastInGroup ? undefined : "none",
                          }}
                        >
                          <p
                            className="truncate max-w-[200px] text-[13px] font-semibold text-gray-800 leading-snug"
                            title={companyName}
                          >
                            {companyName}
                          </p>
                        </td>
                      )}

                      {/* Issuer columns — actual data repeated on every row */}
                      {ISSUER_COLS.map((col) => (
                        <td
                          key={col.key}
                          className={`px-3 py-1.5 whitespace-nowrap text-gray-600 ${
                            col.lastIssuer ? "border-r-2 border-gray-200" : ""
                          } ${col.align === "right" ? "text-right tabular-nums" : ""}`}
                        >
                          <CellValue col={col} row={info} />
                        </td>
                      ))}

                      {/* Position columns */}
                      {POSITION_COLS.map((col, index) => (
                        <td
                          key={col.key}
                          className={`px-3 py-1.5 whitespace-nowrap text-gray-700 ${
                            col.align === "right"
                              ? "text-right tabular-nums"
                              : ""
                          } ${index === POSITION_COLS.length - 1 ? "border-r border-gray-200" : ""}`}
                        >
                          <CellValue col={col} row={pos} />
                        </td>
                      ))}
                    </tr>
                  );
                });
              })}
            </tbody>
          </table>
        </ScrollableX>
      </div>
    </div>
  );
}
